/**
 * Role Catalog Hook (GH #2318)
 *
 * Reads the platform role catalog through the platform-admin spec's
 * `GET /users/roles` operation (`listPlatformRoles`), which platform-admin-api
 * answers by proxying aaim-core `GET /api/v1/roles`. The route sits behind
 * `requireAuthenticatedBearer` only, so the FI-tenant persona (fi_admin)
 * reaches it; IdpRoleMappings, PendingIdpClaims and AbacPolicies read the
 * same operation.
 *
 * Each `RoleSummary.id` is the aaim-core `Role.id` UUID and `name` is the role
 * slug (`RoleRef.slug` on a staff record is the same `Role.name`). Callers
 * resolve slugs to ids from this catalog; a slug the catalog lacks has no id
 * to offer and is omitted, never given a placeholder.
 *
 * The call goes through the typed client so the arc unit that moves this
 * operation to its canonical path (sprint-api-v1-unification B06) finds this
 * caller at type-check time.
 */

import { useQuery } from '@tanstack/react-query';
import { platformAdminClient } from '../api/client';
import type { components } from '@aaimplatform/institutional-types/platform-admin';
import type { components as StaffComponents } from '@aaimplatform/institutional-types/fi-staff-lifecycle';

// ---------------------------------------------------------------------------
// Type aliases from generated OpenAPI schema
// ---------------------------------------------------------------------------

export type RoleSummary = components['schemas']['RoleSummary'];
type RoleRef = StaffComponents['schemas']['RoleRef'];

const ROLE_CATALOG_KEY = 'platform-role-catalog';

// ---------------------------------------------------------------------------
// API helper
// ---------------------------------------------------------------------------

async function fetchRoleCatalog(): Promise<RoleSummary[]> {
  const { data, error, response } = await platformAdminClient.GET('/users/roles');
  if (error || data === undefined) {
    throw new Error(`Role catalog request failed: HTTP ${response.status}`);
  }
  return data.data;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * The platform role catalog. Pass `enabled: false` to defer the request until
 * a surface needs it.
 */
export function useRoleCatalog(options: { enabled?: boolean } = {}) {
  return useQuery<RoleSummary[], Error>({
    queryKey: [ROLE_CATALOG_KEY],
    queryFn: fetchRoleCatalog,
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Assignable staff roles
// ---------------------------------------------------------------------------

/** RoleSummary.tier of tenant-scoped roles, the only tier FI staff hold. */
const TENANT_ROLE_TIER = 2;

/**
 * The roles the FI staff pickers offer (GH #2318, option A2, Robert's ruling
 * of 2026-10-23, confirmed 2026-10-24 20:22Z as the final list): the three FI
 * roles plus the two pilot operator roles. PLEDGE_REVIEWER is the role that
 * works the step-8 Pledge Inbox (pledge:read:inbox, pledge:accept,
 * pledge:decline); PLEDGOR_ADVISOR is the advisor who enters and attests a
 * client's positions. Both are tier 2, so the server's grantable check
 * (`@aaimplatform/auth/grantable`) accepts them as it does the FI roles.
 *
 * Lowercase slugs, the `Role.name` a staff record's RoleRef.slug carries.
 * Invite Staff, Edit Roles and Pending IdP Claims read this one list, so a
 * change here moves all three surfaces. The server allows more (every tier-2
 * role outside its non-grantable names, and tiers 3 and 4); the pickers stay
 * the narrower of the two.
 */
export const STAFF_ROLE_SLUGS: readonly string[] = [
  'fi_admin',
  'fi_loan_officer',
  'fi_compliance',
  'pledge_reviewer',
  'pledgor_advisor',
];

/**
 * The catalog roles an FI admin can give staff, as the staff pickers offer
 * them (GH #2318): each tenant-tier catalog role whose name is on
 * STAFF_ROLE_SLUGS, labeled with the catalog's displayName, sorted by display
 * name. A listed slug the catalog lacks is not offered; no placeholder id is
 * minted.
 *
 * Names compare lowercase (authz-case-insensitive), and the slug is the
 * lowercase name, so an invite stores the same roleSlug form whatever case the
 * catalog carries (it holds names such as FI_LOAN_OFFICER). A name that appears
 * twice with different ids, differing only by case, has no single id to send
 * and is omitted.
 */
export function toAssignableStaffRoles(catalog: readonly RoleSummary[]): RoleRef[] {
  const bySlug = new Map<string, RoleRef | null>();
  for (const role of catalog) {
    if (role.tier !== TENANT_ROLE_TIER) continue;
    const slug = role.name.toLowerCase();
    if (!STAFF_ROLE_SLUGS.includes(slug)) continue;
    const seen = bySlug.get(slug);
    if (seen === undefined) {
      bySlug.set(slug, { id: role.id, slug, name: role.displayName });
    } else if (seen !== null && seen.id !== role.id) {
      bySlug.set(slug, null);
    }
  }
  return [...bySlug.values()]
    .filter((role): role is RoleRef => role !== null)
    .sort((a, b) => a.name.localeCompare(b.name));
}
